import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MoveHorizontal } from 'lucide-react';

export const BeforeAfterComparison = ({ beforeImage, afterImage }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.type.includes('mouse') ? e.clientX : e.touches[0].clientX) - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    const handleGlobalMouseMove = (e) => {
      if (isDragging) handleMove(e);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalMouseMove);
      document.addEventListener('touchend', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalMouseMove);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [isDragging]);

  return (
    <div className="space-y-4">
      {/* Before/After Slider */}
      <div
        ref={containerRef}
        className="relative aspect-[4/3] rounded-xl overflow-hidden shadow-2xl cursor-ew-resize select-none bg-muted"
        onMouseMove={isDragging ? handleMove : undefined}
        onTouchMove={isDragging ? handleMove : undefined}
      >
        {/* After Image (Full) */}
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <img
            src={afterImage}
            alt="After - Professional"
            className="w-full h-full object-contain"
            draggable={false}
          />
          {/* After Label */}
          <div className="absolute top-4 right-4 px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-full shadow-lg">
            After
          </div>
        </div>

        {/* Before Image (Clipped) */}
        <div
          className="absolute inset-0 overflow-hidden flex items-center justify-center"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <img
            src={beforeImage}
            alt="Before - Original"
            className="w-full h-full object-contain"
            draggable={false}
          />
          {/* Before Label */}
          <div className="absolute top-4 left-4 px-3 py-1.5 bg-foreground/80 text-white text-xs font-semibold rounded-full shadow-lg">
            Before
          </div>
        </div>

        {/* Slider Handle */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-white shadow-xl cursor-ew-resize"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
        >
          {/* Handle Circle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="w-12 h-12 bg-white rounded-full shadow-2xl flex items-center justify-center cursor-ew-resize border-2 border-primary"
            >
              <MoveHorizontal className="w-5 h-5 text-primary" />
            </motion.div>
          </div>
        </div>

        {/* Drag Hint - shows on first render */}
        {sliderPosition === 50 && !isDragging && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ delay: 2, duration: 0.5 }}
            className="absolute inset-x-0 bottom-6 flex justify-center pointer-events-none"
          >
            <div className="px-4 py-2 bg-foreground/90 text-white text-sm font-medium rounded-full shadow-lg flex items-center gap-2">
              <MoveHorizontal className="w-4 h-4" />
              Drag to compare
            </div>
          </motion.div>
        )}
      </div>

      {/* Professional Result Only */}
      <div className="max-w-sm mx-auto">
        <div className="aspect-[3/4] rounded-lg overflow-hidden border-2 border-primary shadow-sm bg-muted flex items-center justify-center">
          <img
            src={afterImage}
            alt="Professional headshot"
            className="w-full h-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};
